/**
 * PasswordTimedStepWidget - Widget pour les étapes mot de passe avec timer
 * Identique à Flutter PasswordTimedStepWidget
 */

import React, {useState, useEffect, useRef} from 'react';
import {View, Text, StyleSheet, TextInput, TouchableOpacity, Alert} from 'react-native';
import {usePlayer} from '../../../contexts/PlayerContext';
import {PlayerStep} from '../../../models/PlayerStep';
import {GameStateStatus} from '../../../models/GameState';
import GameStateHelper from '../../../helpers/GameStateHelper';
import {TimeHelper} from '../util/TimeHelper';
import BaseStepWidget from '../../../components/BaseStepWidget';

interface PasswordTimedStepWidgetProps {
  step: PlayerStep;
}

const PasswordTimedStepWidget: React.FC<PasswordTimedStepWidgetProps> = ({step}) => {
  const {gameState, executeAction, refreshCurrentStep} = usePlayer();
  const [passwordInput, setPasswordInput] = useState('');
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitializedRef = useRef(false);

  // Initialiser le timer au chargement de l'étape
  useEffect(() => {
    if (!gameState || hasInitializedRef.current) {
      return;
    }

    // Récupérer le temps initial depuis l'extraField TIMED de l'étape
    const timedField = GameStateHelper.getStepExtraField(gameState, step.numero, 'TIMED');
    const timedValue = timedField?.initValue || timedField?.currentValue || '0';
    const stepTime = parseInt(String(timedValue), 10);

    // Récupérer le temps restant global depuis ESCAPE_TIME_TO_FINISH
    let timeToFinishField = GameStateHelper.getAdventureExtraField(gameState, 'ESCAPE_TIME_TO_FINISH');
    
    // Si ESCAPE_TIME_TO_FINISH n'existe pas, l'initialiser avec une valeur par défaut
    if (!timeToFinishField) {
      // Initialiser avec une valeur par défaut (ex: 300 secondes = 5 minutes)
      GameStateHelper.addOrUpdateAdventureExtraField(gameState, 'ESCAPE_TIME_TO_FINISH', 300);
      timeToFinishField = GameStateHelper.getAdventureExtraField(gameState, 'ESCAPE_TIME_TO_FINISH');
    }

    const currentTime = timeToFinishField
      ? parseInt(String(timeToFinishField.currentValue || timeToFinishField.initValue || '0'), 10)
      : 300;

    // Soustraire le temps de l'étape du temps global (comme dans TimeHelper.substract)
    // Mais seulement si ce n'est pas un "continue" (rechargement de l'étape)
    const lastAction = gameState.data.lastAction || '';
    let finalTime = currentTime;
    
    if (lastAction !== 'continue' && stepTime > 0) {
      finalTime = Math.max(0, currentTime - stepTime);
      GameStateHelper.addOrUpdateAdventureExtraField(gameState, 'ESCAPE_TIME_TO_FINISH', finalTime);
      
      // Mettre à jour le stepExtraField TIMED pour accumuler le temps écoulé
      const stepTimedField = GameStateHelper.getOrCreateGameStateStepExtraField(gameState, step, 'TIMED');
      const currentStepTime = parseInt(String(stepTimedField.currentValue || '0'), 10);
      GameStateHelper.updateGameStateStepExtraFieldValue(gameState, stepTimedField, currentStepTime + stepTime);
    }
    
    setTimeRemaining(finalTime);

    if (finalTime > 0) {
      setIsTimerRunning(true);
      hasInitializedRef.current = true;
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [gameState, step.numero]);

  // Gérer le décompte du timer
  useEffect(() => {
    if (!isTimerRunning || timeRemaining <= 0) {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
      return;
    }

    timerIntervalRef.current = setInterval(() => {
      setTimeRemaining((prev) => {
        const newTime = prev - 1;
        if (newTime <= 0) {
          setIsTimerRunning(false);
          // Le temps est écoulé, marquer le gameState comme FAILED
          handleTimeExpired();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = null;
      }
    };
  }, [isTimerRunning, timeRemaining]);

  // Mettre à jour le temps restant dans gameState
  useEffect(() => {
    if (!gameState || !isTimerRunning) {
      return;
    }

    // Mettre à jour ESCAPE_TIME_TO_FINISH dans gameState
    const timeToFinishField = GameStateHelper.getAdventureExtraField(gameState, 'ESCAPE_TIME_TO_FINISH');
    if (timeToFinishField) {
      GameStateHelper.addOrUpdateAdventureExtraField(gameState, 'ESCAPE_TIME_TO_FINISH', timeRemaining);
    }
  }, [timeRemaining, gameState, isTimerRunning]);

  const handleTimeExpired = async () => {
    if (!gameState) {
      return;
    }

    console.log('[PasswordTimedStepWidget] Time expired, marking gameState as FAILED');
    
    // Mettre à jour le gameState pour le marquer comme FAILED
    gameState.state = GameStateStatus.FAILED;
    
    // Sauvegarder le gameState
    try {
      const gameStateService = require('../../../services/gameStateService').default;
      await gameStateService.updateGameState(gameState);
      await refreshCurrentStep();
    } catch (error) {
      console.error('[PasswordTimedStepWidget] Error refreshing step after time expired:', error);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!passwordInput.trim()) {
      return;
    }

    try {
      // Exécuter l'action password avec le mot de passe saisi
      await executeAction('password', {password: passwordInput.trim()});
      setPasswordInput(''); // Réinitialiser le champ après soumission
    } catch (error: any) {
      console.error('[PasswordTimedStepWidget] Error submitting password:', error);
      // L'erreur sera gérée par l'action password elle-même
    }
  };

  // Déterminer la couleur du timer selon le temps restant
  const getTimerColor = (): string => {
    if (timeRemaining <= 0) {
      return '#d32f2f'; // Rouge si temps écoulé
    }
    if (timeRemaining <= 30) {
      return '#f57c00'; // Orange si moins de 30 secondes
    }
    if (timeRemaining <= 60) {
      return '#fbc02d'; // Jaune si moins d'1 minute
    }
    return '#388e3c'; // Vert sinon
  };

  return (
    <View style={styles.container}>
      {/* Affichage du timer */}
      <View style={styles.timerContainer}>
        <View style={[styles.timerCircle, {borderColor: getTimerColor()}]}>
          <Text style={[styles.timerText, {color: getTimerColor()}]}>
            {TimeHelper.formatTimeRemaining(timeRemaining)}
          </Text>
          <Text style={styles.timerLabel}>Temps restant</Text>
        </View>
      </View>

      {/* Champ de saisie du mot de passe */}
      <View style={styles.passwordContainer}>
        <Text style={styles.passwordLabel}>Mot de passe :</Text>
        <View style={styles.passwordInputContainer}>
          <TextInput
            style={styles.passwordInput}
            value={passwordInput}
            onChangeText={setPasswordInput}
            placeholder="Entrez le mot de passe..."
            placeholderTextColor="#999"
            secureTextEntry
            autoCapitalize="none"
            autoCorrect={false}
            onSubmitEditing={handlePasswordSubmit}
            editable={isTimerRunning && timeRemaining > 0}
          />
          <TouchableOpacity
            style={[styles.submitButton, (!isTimerRunning || timeRemaining <= 0) && styles.submitButtonDisabled]}
            onPress={handlePasswordSubmit}
            disabled={!isTimerRunning || timeRemaining <= 0 || !passwordInput.trim()}>
            <Text style={styles.submitButtonText}>Valider</Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Contenu de l'étape (description, paths, etc.) */}
      <BaseStepWidget step={step} />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  timerContainer: {
    alignItems: 'center',
    padding: 20,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
  },
  timerCircle: {
    width: 120,
    height: 120,
    borderRadius: 60,
    borderWidth: 4,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#fff',
  },
  timerText: {
    fontSize: 24,
    fontWeight: 'bold',
    fontFamily: 'monospace',
  },
  timerLabel: {
    fontSize: 12,
    color: '#666',
    marginTop: 4,
  },
  passwordContainer: {
    padding: 20,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  passwordLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#333',
    marginBottom: 12,
  },
  passwordInputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  passwordInput: {
    flex: 1,
    height: 48,
    borderWidth: 1,
    borderColor: '#8B7355',
    borderRadius: 8,
    paddingHorizontal: 16,
    fontSize: 16,
    backgroundColor: '#fff',
    marginRight: 12,
  },
  submitButton: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    backgroundColor: '#8B7355',
    borderRadius: 8,
  },
  submitButtonDisabled: {
    backgroundColor: '#ccc',
  },
  submitButtonText: {
    color: '#fff',
    fontSize: 16,
    fontWeight: '600',
  },
});

export default PasswordTimedStepWidget;
